import {
  Component, inject, signal, computed, ChangeDetectionStrategy, OnInit, OnDestroy
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TranslocoModule } from '@jsverse/transloco';
import { SelectionModel } from '@angular/cdk/collections';
import { FormsModule } from '@angular/forms';
import { MatDivider } from '@angular/material/divider';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { RelationTableDialogData, RelationTableDialogResult } from './relation-table.types';

@Component({
  selector: 'pixl-relation-table-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogModule, MatTableModule, MatCheckboxModule, MatButtonModule,
    MatFormFieldModule, MatInputModule, MatIconModule, MatPaginatorModule,
    MatProgressBarModule, TranslocoModule, FormsModule, MatDivider, MatSlideToggle
  ],
  templateUrl: './relation-table-dialog.component.html',
  styleUrl: './relation-table-dialog.component.scss'
})
export class RelationTableDialogComponent<T extends object> implements OnInit, OnDestroy {
  private dialogRef = inject(MatDialogRef<RelationTableDialogComponent<T>>);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: RelationTableDialogData<T> = inject<any>(MAT_DIALOG_DATA);

  items = signal<T[]>([]);
  total = signal(0);
  page = signal(0);
  pageSize = signal(20);
  searchQuery = signal('');
  isLoading = signal(false);

  selection = new SelectionModel<T>(true, []);

  displayedColumns = computed(() => {
    const cols = this.data.columns.map(c => c.key);
    return ['select', ...cols];
  });

  showSelectedOnly = signal(false);
  private selectionVersion = signal(0);
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  filteredItems = computed(() => {
    this.selectionVersion();
    if (!this.showSelectedOnly()) {
      return this.items();
    }
    return this.selection.selected;
  });

  ngOnInit(): void {
    this.selection = new SelectionModel<T>(
      this.data.multiple,
      this.data.selectedItems
    );

    this.searchSubject.pipe(
      debounceTime(300),
      takeUntil(this.destroy$)
    ).subscribe(query => {
      this.searchQuery.set(query);
      this.page.set(0);
      this.loadData();
    });

    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadData(): Promise<void> {
    this.isLoading.set(true);
    const result = await this.data.loadFn({
      page: this.page() + 1,
      pageSize: this.pageSize(),
      search: this.searchQuery()
    });
    this.items.set(result.data);
    this.total.set(result.total);
    this.isLoading.set(false);
  }

  onPageChange(event: PageEvent): void {
    this.page.set(event.pageIndex);
    this.pageSize.set(event.pageSize);
    this.loadData();
  }

  onSearch(query: string): void {
    this.searchSubject.next(query);
  }

  isSelected(row: T): boolean {
    const idKey = this.data.idKey || 'id';
    return this.selection.selected.some(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      s => (s as any)[idKey] === (row as any)[idKey]
    );
  }

  toggleRow(row: T): void {
    if (this.isSelected(row)) {
      const idKey = this.data.idKey || 'id';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const existing = this.selection.selected.find(s => (s as any)[idKey] === (row as any)[idKey]);
      if (existing) {
        this.selection.deselect(existing);
      }
    }
    else {
      this.selection.select(row);
    }
    this.selectionVersion.update(v => v + 1);
  }

  toggleShowSelected(): void {
    this.showSelectedOnly.update(v => !v);
    this.selectionVersion.update(v => v + 1);
  }

  confirm(): void {
    this.dialogRef.close({ selectedItems: this.selection.selected } as RelationTableDialogResult<T>);
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
